import React, {useCallback, useEffect, useState} from "react";
import {authenticationApi} from "../../authentication/api/authentication.api";
import {sessionStorageService} from "../../authentication/services/session.storage";
import {listBrokers} from "../api/admin";
import ReviewQueue from "./ReviewQueue";
import BrokersTable from "./BrokersTable";
import SiteStaffApprovals from "./SiteStaffApprovals";
import AgentsTable from "./AgentsTable";
import SitesTable from "./SitesTable";
import LiveBoard from "./LiveBoard";
import OfficeLeads from "./OfficeLeads";
import "../styles/brokerage.css";
import "../styles/workspace.css";
import "../styles/admin.css";

/**
 * Admin: the review queue (listings, projects, edits) and broker accounts. An admin is an ordinary site account with
 * is_admin (set with scripts/promote_admin.py), signed in here with the site login.
 */
const AdminPage = () => {
    // "checking" | "login" | "forbidden" | "ready"
    const [phase, setPhase] = useState(sessionStorageService.get() ? "checking" : "login");
    const [tab, setTab] = useState("review");
    const [currentEmail, setCurrentEmail] = useState("");
    const [login, setLogin] = useState({email: "", password: "", error: "", busy: false});

    const onAuthError = useCallback(async (err) => {
        if (err.status === 401) {
            sessionStorageService.remove();
            setPhase("login");
            setLogin((l) => ({...l, error: "Your session expired. Please log in again."}));
            return true;
        }
        if (err.status === 403 || err.status === 404) {
            try {
                setCurrentEmail((await authenticationApi.getCurrentUser()).email || "");
            } catch {
                setCurrentEmail("");
            }
            setPhase("forbidden");
            return true;
        }
        return false;
    }, []);

    const check = useCallback(async () => {
        try {
            await listBrokers("PENDING");
            setPhase("ready");
        } catch (err) {
            if (!(await onAuthError(err))) setPhase("ready");
        }
    }, [onAuthError]);

    useEffect(() => { if (phase === "checking") check(); }, [phase, check]);

    const submitLogin = async (e) => {
        e.preventDefault();
        setLogin((l) => ({...l, error: "", busy: true}));
        try {
            const {token} = await authenticationApi.login(login.email.trim(), login.password);
            sessionStorageService.set(token);
            setLogin({email: "", password: "", error: "", busy: false});
            setPhase("checking");
        } catch (err) {
            setLogin((l) => ({...l, busy: false, error: err.message || "Login failed. Please try again."}));
        }
    };

    const signOut = () => {
        sessionStorageService.remove();
        setPhase("login");
    };

    if (phase === "login" || phase === "checking") {
        return (
            <div className="broker-page">
                <div className="broker-header">
                    <h1 className="broker-header__title">Admin</h1>
                    <p className="broker-header__subtitle">Partner approvals and listing review</p>
                </div>
                <div className="broker-card admin-key-gate">
                    {phase === "checking" ? <p>Checking your access…</p> : (
                        <form onSubmit={submitLogin}>
                            <p>Log in with an admin account.</p>
                            {login.error && <p className="broker-otp__error">⚠ {login.error}</p>}
                            <label className="brk-field">
                                <span className="brk-field__label">Email</span>
                                <input className="brk-input" type="email" autoComplete="username" value={login.email}
                                       onChange={(e) => setLogin((l) => ({...l, email: e.target.value}))}/>
                            </label>
                            <label className="brk-field">
                                <span className="brk-field__label">Password</span>
                                <input className="brk-input" type="password" autoComplete="current-password" value={login.password}
                                       onChange={(e) => setLogin((l) => ({...l, password: e.target.value}))}/>
                            </label>
                            <button type="submit" className="brk-btn brk-btn--primary" disabled={login.busy}>
                                {login.busy ? "Logging in…" : "Log in"}
                            </button>
                        </form>
                    )}
                </div>
            </div>
        );
    }

    if (phase === "forbidden") {
        return (
            <div className="broker-page">
                <div className="broker-card admin-key-gate">
                    <p className="broker-otp__error">
                        ⚠ {currentEmail ? <>The account <strong>{currentEmail}</strong> doesn't</> : "This account doesn't"} have admin access.
                    </p>
                    <p>Ask an existing admin to run <code>scripts/promote_admin.py</code> for this account, or log in as a different one.</p>
                    <button type="button" className="brk-btn brk-btn--primary" onClick={signOut}>Log in as a different account</button>
                </div>
            </div>
        );
    }

    return (
        <div className="brk-shell">
            <main className="brk-main">
                <div className="brk-page-head">
                    <div>
                        <h1 className="brk-page-title">Partner admin</h1>
                        <p className="brk-page-subtitle">Approve partners and review what they publish</p>
                    </div>
                    <button type="button" className="brk-btn brk-btn--ghost brk-btn--sm" onClick={signOut}>Sign out</button>
                </div>
                <nav className="brk-tabs" role="tablist" aria-label="Admin sections">
                    {[["review", "Review queue"], ["brokers", "Brokers & builders"], ["staff", "Site staff approvals"], ["leads", "Leads"], ["live", "Live board"], ["agents", "Agents"], ["sites", "Sites"]].map(([id, label]) => (
                        <button key={id} role="tab" aria-selected={tab === id} className={`brk-tab${tab === id ? " brk-tab--on" : ""}`}
                                onClick={() => setTab(id)}>{label}</button>
                    ))}
                </nav>
                <div className="brk-card">
                    {tab === "review" && <ReviewQueue onAuthError={onAuthError}/>}
                    {tab === "brokers" && <BrokersTable onAuthError={onAuthError}/>}
                    {tab === "staff" && <SiteStaffApprovals onAuthError={onAuthError}/>}
                    {tab === "leads" && <OfficeLeads onAuthError={onAuthError}/>}
                    {tab === "live" && <LiveBoard onAuthError={onAuthError}/>}
                    {tab === "agents" && <AgentsTable onAuthError={onAuthError}/>}
                    {tab === "sites" && <SitesTable onAuthError={onAuthError}/>}
                </div>
            </main>
        </div>
    );
};

export default AdminPage;
